import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

/** Compact currency for stat tiles: 12.9K / 4.2L / 1.1Cr (Indian conventions). */
export function formatCurrency(value) {
    const number = Number(value) || 0;
    const sign = number < 0 ? '-' : '';
    const abs = Math.abs(number);
    if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(2)} L`;
    if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(1)}K`;
    return `${sign}₹${abs.toFixed(0)}`;
}

export function formatFullCurrency(value) {
    return `₹${(Number(value) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/**
 * Stat tile: label, value, optional sub-line.
 *
 * Values use the default proportional figures - tabular-nums makes a number
 * like 121 look loose at display sizes, and these are standalone, not columns.
 */
export function StatTile(props) {
    const { label, value, sub, tone, tokens } = props;
    const toneColor = tone === 'good' ? tokens.good
        : tone === 'critical' ? tokens.critical
            : tokens.textPrimary;

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                    {label}
                </Typography>
                <Typography
                    component="div"
                    sx={{
                        mt: 0.5,
                        fontSize: { xs: '1.6rem', sm: '1.9rem' },
                        fontWeight: 600,
                        lineHeight: 1.15,
                        color: toneColor,
                    }}
                >
                    {value}
                </Typography>
                {sub && (
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        {sub}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * Chart card with a built-in table-view twin.
 *
 * Every chart ships one: it is the WCAG-clean equivalent, and it is what
 * discharges the sub-3:1 contrast relief rule for the lighter categorical hues.
 */
export function ChartCard(props) {
    const { title, subtitle, tokens, children, tableHead, tableRows, height = 300 } = props;
    const [showTable, setShowTable] = useState(false);
    const hasTable = Boolean(tableHead && tableRows);

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            {/* Tighter gutters on phone: 16px of card padding either side is
                8% of a 375px screen, and charts need that width more. */}
            <CardContent sx={{
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } },
            }}>
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start',
                    justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap',
                }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
                        {subtitle && (
                            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    {hasTable && (
                        // ml:auto keeps this hard right even when a long title
                        // pushes it onto its own line at phone width.
                        <Button
                            size="small"
                            onClick={() => setShowTable((prev) => !prev)}
                            sx={{ ml: 'auto', flexShrink: 0 }}
                        >
                            {showTable ? 'Show chart' : 'Show table'}
                        </Button>
                    )}
                </Box>

                {showTable && hasTable ? (
                    <Box sx={{ maxHeight: height, overflow: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {tableHead.map((heading, index) => (
                                        <TableCell
                                            key={heading}
                                            align={index === 0 ? 'left' : 'right'}
                                        >
                                            {heading}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableRows.map((row, rowIndex) => (
                                    <TableRow key={`row-${rowIndex}`}>
                                        {row.map((cell, cellIndex) => (
                                            <TableCell
                                                key={`cell-${cellIndex}`}
                                                align={cellIndex === 0 ? 'left' : 'right'}
                                                // Columns of numbers do align vertically here,
                                                // so tabular figures are correct in the table.
                                                sx={cellIndex === 0
                                                    ? undefined
                                                    : { fontVariantNumeric: 'tabular-nums' }}
                                            >
                                                {cell}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ) : (
                    <Box sx={{ width: '100%' }}>{children}</Box>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * Ranked bars for narrow screens.
 *
 * A horizontal bar chart puts long category names in a left gutter. At phone
 * width that gutter ate 48% of the plot on "Best sellers" - half the chart was
 * axis labels. Stacking the name above its bar gives the label the full width
 * and the bar the full width, which is why this is the mobile form and the
 * chart stays on larger screens.
 *
 * Still a bar chart: one hue for nominal categories (never a value ramp), bars
 * grown from a common baseline, and every value directly labelled.
 */
export function RankedBarList(props) {
    const { rows, tokens, valueFormatter, colorFor, max } = props;
    if (!rows || rows.length === 0) return null;

    const peak = max || Math.max(...rows.map((r) => r.value), 0) || 1;
    const format = valueFormatter || ((v) => v);

    return (
        <Box sx={{ mt: 1 }}>
            {rows.map((row) => (
                <Box key={row.label} sx={{ mb: 1.5 }}>
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'baseline', gap: 1, mb: 0.5,
                    }}>
                        <Typography variant="body2" sx={{
                            minWidth: 0, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {row.label}
                        </Typography>
                        <Typography variant="body2" sx={{
                            fontWeight: 600, whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums', color: tokens.textPrimary,
                        }}>
                            {format(row.value)}
                        </Typography>
                    </Box>
                    {/* Track is one step off the surface so an empty bar still
                        reads as "measured, and small" rather than missing. */}
                    <Box sx={{
                        height: 8, borderRadius: '4px',
                        backgroundColor: tokens.grid, overflow: 'hidden',
                    }}>
                        <Box sx={{
                            height: '100%',
                            width: `${Math.max(2, (row.value / peak) * 100)}%`,
                            borderRadius: '4px',
                            backgroundColor: colorFor ? colorFor(row) : tokens.magnitude,
                        }} />
                    </Box>
                    {row.sub && (
                        <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                            {row.sub}
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
}

/** Legend rendered as swatch + text token - never colour the text itself. */
export function Legend(props) {
    const { items, tokens } = props;
    if (!items || items.length < 2) return null;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            {items.map((item) => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{
                        width: 10, height: 10, borderRadius: '2px',
                        backgroundColor: item.color, flexShrink: 0,
                    }} />
                    <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                        {item.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

export function EmptyState(props) {
    const { title, children, tokens } = props;
    return (
        <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            <Typography variant="body2" sx={{ color: tokens.textSecondary, maxWidth: 560, mx: 'auto' }}>
                {children}
            </Typography>
        </Box>
    );
}
